import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Table, Thead, Tbody, Tr, Th, Td, Switch, Box, Flex, Text, Spinner, IconButton, Button, Input, InputGroup, InputLeftElement, InputRightElement, useToast, Badge } from "@chakra-ui/react";
import { SearchIcon, CloseIcon, DownloadIcon } from "@chakra-ui/icons";
import { FaEdit, FaSave } from "react-icons/fa";
import ExcelJS from "exceljs";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";
import { usePageAccess } from "../../../wrappers/PageAccessContext";

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  // 403 from the org-admin route = a supplier this org admin can see but does not
  // administer (Blue Mango's Spear guests, 2026-10-02) — say so instead of an empty table.
  const [blocked, setBlocked] = useState("");
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(false);
  // Keyed on articlecode, NOT on the row's position. The rendered list is
  // filtered by the search box, so an index would point at a different article
  // than the one the user clicked edit on — and handleSave would write the
  // edited values to that other article.
  const [editRowCode, setEditRowCode] = useState(null);
  // 2026-10-17 (Helena): the edit box takes unit cost EXCL + pack size; incl (articleunitcost — the price every
  // calculation uses) and the case costs are computed by the server.
  const [editRowData, setEditRowData] = useState({ brand: "", unitcost_excl: "", pack_size: "", articledescription: "", vendor_product_code: "" });
  const money = (v, dp = 2) => (v == null || v === "" ? "" : Number(v).toFixed(dp));
  const { selectedRetailer, selectedSupplier, selectedSupplierRow, isMaster } = useSupplierRetailer();
  const toast = useToast();

  // Org admins use the org-scoped endpoints (supplier/retailer validated
  // server-side). Since 2026-09-16 they edit the same four fields as masters —
  // brand, description, cost, active. The Articles page grant is the gate.
  const isOrgAdmin = localStorage.getItem("user_type") === "org_admin";
  const BASE = isOrgAdmin ? "org-admin" : "admin";

  // Read-only grant (2026-09-17): the master can hand a CAM / org admin the
  // Articles page as view-only. `level` rides on the page row from
  // /login/get--user-access; the backend refuses the writes with 403 regardless
  // of what this page renders, so hiding the controls is courtesy, not the gate.
  const { user_page_access } = usePageAccess();
  const levelReadOnly = (user_page_access || []).some((p) => p.value === "Articles" && p.level === "ro");
  // Spear Management's own suppliers (home or guest — ALETTES, ADRIATIC, CARING
  // CANDY, NATURES CHOICE …) are edited by masters only, whatever the level
  // (2026-09-17). `orgs` rides on the supplier row from /login/get--user-access.
  const supplierOrgs = selectedSupplierRow ? (Array.isArray(selectedSupplierRow.orgs) && selectedSupplierRow.orgs.length ? selectedSupplierRow.orgs : [selectedSupplierRow.org]) : [];
  const spearManaged = !isMaster && supplierOrgs.includes("SPEARMGMT");
  const readOnly = levelReadOnly || spearManaged;

  useEffect(() => {
    if (selectedSupplier && selectedRetailer) {
      fetchArticles();
    }
  }, [selectedSupplier, selectedRetailer]);

  // Leaving a half-finished edit open across a supplier switch would show one
  // article's values against another's row.
  useEffect(() => {
    setEditRowCode(null);
    setSearch("");
  }, [selectedSupplier, selectedRetailer]);

  const fetchArticles = async () => {
    setLoading(true);
    setBlocked("");
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/${BASE}/get-articles`, {
        suppliercode: selectedSupplier,
        retailercode: selectedRetailer,
      });
      setArticles(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching articles:", error);
      if (isOrgAdmin && error?.response?.status === 403) {
        setArticles([]);
        setBlocked(spearManaged ? `${selectedSupplier} is managed by Spear — its articles are not available here.` : error?.response?.data?.message || "Not available for this supplier.");
      }
      setLoading(false);
    }
  };

  // The API now returns these ordered by description, but sorting here too means
  // the page reads alphabetically even against a backend that has not been
  // updated yet — the two deploy separately.
  const sortedArticles = useMemo(() => {
    return [...articles].sort((a, b) => {
      const byDescription = String(a.articledescription ?? "").localeCompare(String(b.articledescription ?? ""), undefined, { sensitivity: "base" });
      if (byDescription !== 0) return byDescription;
      // Repackaged sizes share a description; the code keeps their order stable.
      return String(a.articlecode ?? "").localeCompare(String(b.articlecode ?? ""));
    });
  }, [articles]);

  const visibleArticles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedArticles;
    return sortedArticles.filter(
      (article) =>
        String(article.articlecode ?? "").toLowerCase().includes(q) ||
        String(article.articledescription ?? "").toLowerCase().includes(q)
    );
  }, [sortedArticles, search]);

  const toggleActiveYn = async (articlecode, activeyn) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/${BASE}/toggle-article`, {
        suppliercode: selectedSupplier,
        retailercode: selectedRetailer,
        articlecode,
        activeyn: !activeyn,
      });
      setArticles((prevArticles) => prevArticles.map((article) => (article.articlecode === articlecode ? { ...article, activeyn: !activeyn } : article)));
    } catch (error) {
      console.error("Error updating article status:", error);
    }
  };

  const handleEdit = (article) => {
    setEditRowCode(article.articlecode);
    setEditRowData({
      brand: article.brand,
      unitcost_excl: article.unitcost_excl != null ? Number(article.unitcost_excl).toFixed(2) : (article.articleunitcost != null && article.articleunitcost !== "" ? (Number(article.articleunitcost) / 1.15).toFixed(2) : ""),
      pack_size: article.pack_size ?? "",
      vendor_product_code: article.vendor_product_code ?? "",
      articledescription: article.articledescription,
    });
  };

  const handleSave = async () => {
    const { brand, unitcost_excl, pack_size, articledescription, vendor_product_code } = editRowData;
    const articlecode = editRowCode;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/${BASE}/upd-article`, {
        brand,
        unitcost_excl,
        pack_size,
        vendor_product_code,
        articledescription,
        suppliercode: selectedSupplier,
        retailercode: selectedRetailer,
        articlecode,
      });
      toast({
        title: "Article updated",
        description: "The article has been updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setEditRowCode(null);
      fetchArticles();
    } catch (error) {
      toast({
        title: "Failed to update article",
        description: "An error occurred while updating the article.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Excel dump of the whole articles table for the selected supplier + retailer.
  // Built from the rows already in memory (the API returns SELECT *), so the
  // export carries the two columns the on-screen table hides — the PnP/CHK
  // vendor code and the PnP cost-price "valid from" date — without a backend
  // change. The articles tables hold no case cost / excl-VAT columns: unit cost
  // is the only price stored (PnP = case cost incl ÷ pack size at import).
  const downloadArticles = async () => {
    if (!sortedArticles.length) return;
    setDownloading(true);
    try {
      const hasArticleDate = sortedArticles.some((a) => a.articledate != null);
      const toDate = (v) => (v ? new Date(v) : null);
      const n = (v) => (v == null || v === "" ? null : Number(v));

      // Column order per Helena (2026-10-17): Retailer, Vendor code, Supplier, Article code, Description, Brand, Case
      // config, Unit cost excl / incl, Case cost excl / incl, then the rest. The file reads back into the Articles upload.
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet("Articles");
      ws.columns = [
        { header: "Retailer", key: "retailercode", width: 10 },
        { header: "Vendor code", key: "vendorcode", width: 14 },
        { header: "Vendor product code", key: "vendor_product_code", width: 16 },
        { header: "Supplier", key: "suppliercode", width: 16 },
        { header: "Retailer article code", key: "articlecode", width: 18 },
        { header: "Article description", key: "articledescription", width: 48 },
        { header: "BRAND", key: "brand", width: 18 },
        { header: "Case config", key: "pack_size", width: 11 },
        { header: "Cost price excl", key: "unitcost_excl", width: 14 },
        { header: "Unit cost incl", key: "articleunitcost", width: 14 },
        { header: "Case cost excl", key: "casecost_excl", width: 14 },
        { header: "Case cost incl", key: "casecost_incl", width: 14 },
        ...(hasArticleDate ? [{ header: "Cost valid from", key: "articledate", width: 16 }] : []),
        { header: "Created", key: "createddate", width: 12 },
        { header: "Active", key: "activeyn", width: 8 },
      ];
      sortedArticles.forEach((a) => {
        ws.addRow({
          retailercode: String(a.retailercode ?? selectedRetailer ?? "").toUpperCase(),
          vendorcode: a.vendorcode_effective ?? a.vendorcode ?? "",
          vendor_product_code: a.vendor_product_code ?? "",
          suppliercode: a.suppliercode ?? selectedSupplier,
          articlecode: a.articlecode,
          articledescription: a.articledescription,
          brand: a.brand,
          pack_size: n(a.pack_size),
          unitcost_excl: n(a.unitcost_excl),
          articleunitcost: n(a.articleunitcost),
          casecost_excl: n(a.casecost_excl),
          casecost_incl: n(a.casecost_incl),
          articledate: toDate(a.articledate),
          createddate: toDate(a.createddate),
          activeyn: a.activeyn ? "Yes" : "No",
        });
      });
      ws.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      ws.getRow(1).eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58B6C0" } };
      });
      ws.views = [{ state: "frozen", ySplit: 1 }];
      ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: ws.columns.length } };
      ["unitcost_excl", "articleunitcost"].forEach((k) => { ws.getColumn(k).numFmt = "#,##0.00"; });
      ["casecost_excl", "casecost_incl"].forEach((k) => { ws.getColumn(k).numFmt = "#,##0.00"; });
      ws.getColumn("createddate").numFmt = "yyyy-mm-dd";
      if (hasArticleDate) ws.getColumn("articledate").numFmt = "yyyy-mm-dd";

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      link.download = `Articles_${selectedSupplier}_${String(selectedRetailer).toUpperCase()}_${stamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Articles export error:", error);
      toast({ title: "Download failed", description: "Could not build the articles file.", status: "error", duration: 3000, isClosable: true });
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box mt={16} maxWidth="97vw">
      {/* Filters as you type — the list is already in memory, so there is nothing
          to wait for and no reason to make the user press Enter. */}
      <Flex mb={3} align="center" gap={3} wrap="wrap">
        <InputGroup size="sm" width="320px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search article code or description"
            aria-label="Search articles"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setSearch("");
            }}
            borderColor="gray.300"
            borderRadius="md"
          />
          {search && (
            <InputRightElement>
              <IconButton aria-label="Clear search" icon={<CloseIcon boxSize={2.5} />} size="xs" variant="ghost" onClick={() => setSearch("")} />
            </InputRightElement>
          )}
        </InputGroup>
        <Text fontSize="sm" color="gray.500">
          {blocked || (search ? `${visibleArticles.length} of ${articles.length} articles` : `${articles.length} articles`)}
        </Text>
        {readOnly && (
          <Badge
            colorScheme="orange"
            variant="subtle"
            title={spearManaged && !levelReadOnly ? "This supplier is managed by Spear Management — only a Spear master admin edits its articles." : "Your access to this page is view-only. Ask a Spear master admin to change it."}
          >
            {spearManaged && !levelReadOnly ? "Read only · managed by Spear" : "Read only"}
          </Badge>
        )}
        <Button
          size="sm"
          ml="auto"
          leftIcon={<DownloadIcon />}
          colorScheme="teal"
          variant="outline"
          onClick={downloadArticles}
          isLoading={downloading}
          loadingText="Building…"
          isDisabled={articles.length === 0}
        >
          Download Excel
        </Button>
      </Flex>

      <Box overflowX="auto" maxHeight="900px" overflowY="auto">
        <Table variant="striped" size="sm" colorScheme="custom" border="1px" borderColor="gray.200" borderRadius="md">
          <Thead>
            <Tr>
              <Th>Article Code</Th>
              <Th>Description</Th>
              <Th>Vendor code</Th>
              <Th>Vendor product code</Th>
              <Th isNumeric>Pack size</Th>
              <Th isNumeric>Unit cost excl</Th>
              <Th isNumeric>Unit cost incl</Th>
              <Th isNumeric>Case cost excl</Th>
              <Th isNumeric>Case cost incl</Th>
              <Th>Brand</Th>
              <Th>Created Date</Th>
              <Th>Active</Th>
              {!readOnly && <Th>Edit</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {visibleArticles.map((article) => {
              const isEditing = editRowCode === article.articlecode;
              return (
                <Tr key={article.articlecode}>
                  <Td>{article.articlecode}</Td>
                  <Td>{isEditing ? <Input value={editRowData.articledescription} onChange={(e) => setEditRowData({ ...editRowData, articledescription: e.target.value })} /> : article.articledescription}</Td>
                  <Td>{article.vendorcode_effective ?? article.vendorcode ?? ""}</Td>
                  {/* the supplier's own code (2026-10-21): from the PnP files, or typed here / in the articles upload */}
                  <Td>{isEditing ? <Input size="sm" w="110px" value={editRowData.vendor_product_code} onChange={(e) => setEditRowData({ ...editRowData, vendor_product_code: e.target.value })} /> : article.vendor_product_code ?? ""}</Td>
                  <Td isNumeric>{isEditing ? <Input size="sm" w="70px" value={editRowData.pack_size} onChange={(e) => setEditRowData({ ...editRowData, pack_size: e.target.value })} /> : article.pack_size ?? ""}</Td>
                  <Td isNumeric>{isEditing ? <Input size="sm" w="100px" value={editRowData.unitcost_excl} onChange={(e) => setEditRowData({ ...editRowData, unitcost_excl: e.target.value })} /> : money(article.unitcost_excl)}</Td>
                  <Td isNumeric title="VAT-incl unit cost — the price every calculation uses">{money(article.articleunitcost)}</Td>
                  <Td isNumeric>{money(article.casecost_excl)}</Td>
                  <Td isNumeric>{money(article.casecost_incl)}</Td>
                  <Td>{isEditing ? <Input value={editRowData.brand} onChange={(e) => setEditRowData({ ...editRowData, brand: e.target.value })} /> : article.brand}</Td>
                  <Td>{article.createddate ? new Date(article.createddate).toLocaleDateString() : ""}</Td>
                  <Td>
                    <Switch isChecked={article.activeyn} isDisabled={readOnly} onChange={() => toggleActiveYn(article.articlecode, article.activeyn)} />
                  </Td>
                  {!readOnly && (
                    <Td>{isEditing ? <IconButton aria-label="Save article" icon={<FaSave />} onClick={handleSave} colorScheme="green" variant="ghost" /> : <IconButton aria-label="Edit article" icon={<FaEdit />} onClick={() => handleEdit(article)} colorScheme="blue" variant="ghost" />}</Td>
                  )}
                </Tr>
              );
            })}
            {visibleArticles.length === 0 && (
              <Tr>
                <Td colSpan={readOnly ? 11 : 12}>
                  <Text fontSize="sm" color="gray.500" py={3}>
                    No articles match “{search}”.
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default Articles;
